import React, {useCallback, useState} from 'react';
import {Template} from "../../../../components/template/template.jsx";
import Button from "../../../../components/button/button.jsx";
import {FaPlus} from "react-icons/fa";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import {useParams} from "react-router";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import InputDateTime from "../../../../components/input/input-datetime.jsx";
import {useQuery} from "@tanstack/react-query";
import assetActivesService from "../../../../services/web/asset-actives-service.js";
import projectUtils from "../../../../utils/project-utils.js";
import Swal from "sweetalert2";
import Attach from "../../../../components/attach/attach.jsx";
import AsyncInput from "../../../../components/input/async-input.jsx";
import SentryRow from "../../../../components/template/sentry-row.jsx"
import {useGlobalKey} from "../../../../hooks/use-global-key.js";

const initialState = {
    description: "",
    asset_number: "",
    serial_number: "",
    assetPlaces: "",
    brand: "",
    model: "",
    status: "",
    type: "",
    supplier: "",
    warranty: "",
    asset_file: "",
    asset_file_attach: "",
}

const statusOpt = [
    {value: "active", label: "Ativo"},
    {value: "decrease", label: "Baixado"}
]

const typeOpt = [
    {value: "electronic", label: "Eletrônico"},
    {value: "furniture", label: "Mobiliário"}
]

const AssetActivesForm = () => {
    const navigate = navigateHook();

    const {id} = useParams()
    const [state, setState] = useState(initialState)
    const [placesOpt, setPlacesOpt] = useState([])

    const onChange = ({target: {value, id}}) => setState(prev => ({...prev, [id]: value}))

    const handleInputs = (id, value) => setState(prev => ({...prev, [id]: value}));

    const handleDate = useCallback((value, field) => {
        setState(prev => ({
            ...prev,
            [field]: value || null
        }));
    }, []);

    const { refetch: refetchAsset, isLoading: isLoadingAsset} = useQuery({
        queryFn: () =>  assetActivesService.get(id).then((data) => {
            setState({
                description: data.description,
                asset_number: data.asset_number,
                serial_number: data.serial_number,
                assetPlaces: data.asset_places_id,
                brand: data.brand,
                model: data.model,
                status: data.status,
                type: data.type,
                supplier: data.supplier,
                warranty: data.warranty,
                asset_file: data.asset_file,
                asset_file_attach: data.asset_file,
            })

            return []
        }),
        queryKey: ['asset'],
        enabled: !!id,
    });

   useQuery({
        queryFn: () =>  assetActivesService.getPlaces().then(({data}) => {
            const newData = []
            data.map(d => newData.push({ value: d.id, label: d.text }));

            setPlacesOpt(newData)
            return []
        }),
        queryKey: ['places']
    });

    const save = () => {
        const formData = new FormData();

        Object.keys(state)
            .filter(key => !["asset_file", "assetPlaces", "warranty"].includes(key))
            .forEach(key => {
                let data = state[key];
                if (typeof data === 'object') data = data?.value ?? "";

                formData.append(key, data);
            });

        if (!!id) formData.append('_method', 'PUT');

        const formatedData = state.warranty ? projectUtils.formatDate(state.warranty, "DD/MM/YYYY") : ""
        formData.append("warranty", formatedData)

        if (state.assetPlaces) formData.append("asset_places_id", state.assetPlaces)

        if(state.asset_file && !state.asset_file_attach) formData.append("asset_file", state.asset_file);

        const response = !!id ? assetActivesService.edit(id, formData) : assetActivesService.create(formData);
        response
            .then(({data}) => {
                projectUtils.success(!id);
                if (id) {
                    refetchAsset()
                    return;
                }

                navigateToEdit(data)
            })
            .catch((error) => {
                console.error(error);

                const {message} = error.response.data;

                let errMessage = message ?? "Não foi possível salvar.";
                projectUtils.swalErrorMessage("", errMessage, "Ok");
            })

    }

    const destroy = () => {
        Swal.fire({
            title: "Excluir",
            text: `Excluir o ativo "${state.description}"?`,
            icon: "warning",
            confirmButtonText: "Sim",
            confirmButtonColor: "#063A6C",
            cancelButtonText: "Não",
            cancelButtonColor: "#c10007",
            showCancelButton: true,
            allowOutsideClick: false,
            allowEscapeKey: false,
            reverseButtons: true
        }).then(({isConfirmed}) => {
            if (isConfirmed) {
                assetActivesService.destroy(id).then(() => {
                    navigateToList()
                    projectUtils.success(true, "Excluído com sucesso");
                })
            }
        });
    }

    const navigateToList = () => {
        let isBlank = false;
        let urlDev = `/asset-actives`;
        navigate(urlDev, isBlank);
    }

    const newAsset = () => {
        let isBlank = false;
        let urlDev = `/asset-actives/form`;
        navigate(urlDev, isBlank);
    }

    const navigateToEdit = (data) => {
        let isBlank = false;
        let urlDev = `/asset-actives/form/${data.id}`;
        navigate(urlDev, isBlank);
    }

    const onChangeFile = (file, id) => {
        if (file) {
            setState(prev => ({...prev, [id]: file}))
        }
    }

    const removeFile = (id) => {
        setState(prev => ({...prev, [id]: "", asset_file_attach: null}))
    }

    const getAssetAutoComplete = (search) => {
        if (search === '') {
            return Promise.resolve([]);
        }

        return assetActivesService.getAutoComplete(search)
            .then((data) => {
                return data.map(({description}) => ({value: description, label: description}));
            })
            .catch((error) => {
                return [];
            });
    }

    return (
        <Template isLoading={isLoadingAsset}>
            <Template.Header>
                <Template.Title>
                    Ativo
                </Template.Title>
                {
                    id &&
                    <>
                        <Button target={"primary-outline"} onClick={() => newAsset()}>
                            <FaPlus/>
                            Adicionar ativo
                        </Button>
                    </>
                }
            </Template.Header>

            <Template.Body>
                <Card>
                    <Card.Body>
                        <div className="col-span-10 relative">
                            <SentryRow className={"mb-3"}>
                                <div className="col-span-6">
                                    <AsyncInput
                                        id="description"
                                        name="description"
                                        label="Descrição *"
                                        value={state.description}
                                        onChange={onChange}
                                        fetchData={getAssetAutoComplete}
                                    />
                                </div>
                                <div className="col-span-2">
                                    <InputForm id="asset_number" value={state.asset_number} onChange={onChange}
                                               label="Nº Patrimônio"/>
                                </div>
                                <div className="col-span-2">
                                    <InputForm id="serial_number" value={state.serial_number} onChange={onChange}
                                               label="Nº Série"/>
                                </div>
                                <div className="col-span-2">
                                    <SelectComponent
                                        id={"assetPlaces"}
                                        name="assetPlaces"
                                        value={placesOpt.find(t => t.value === state?.assetPlaces)}
                                        options={placesOpt}
                                        label={"Local *"}
                                        onChange={(selected) => handleInputs("assetPlaces", selected?.value)}
                                    />
                                </div>

                                <div className="col-span-4">
                                    <InputForm id="brand" value={state.brand} onChange={onChange}
                                               label="Marca"/>
                                </div>
                                <div className="col-span-4">
                                    <InputForm id="model" value={state.model} onChange={onChange}
                                               label="Modelo"/>
                                </div>
                                <div className="col-span-2">
                                    <SelectComponent
                                        name="status"
                                        value={statusOpt.find(t => t.value === state?.status)}
                                        options={statusOpt}
                                        label={"Status"}
                                        onChange={(selected) => handleInputs("status", selected?.value)}
                                    />
                                </div>
                                <div className="col-span-2">
                                    <SelectComponent
                                        name="type"
                                        value={typeOpt.find(t => t.value === state?.type)}
                                        options={typeOpt}
                                        label={"Tipo"}
                                        onChange={(selected) => handleInputs("type", selected?.value)}
                                    />
                                </div>


                                <div className="col-span-10">
                                    <InputForm id="supplier" value={state.supplier} onChange={onChange}
                                               label="Fornecedor"/>
                                </div>
                                <div className="col-span-2">
                                    <InputDateTime
                                        id={"warranty"}
                                        selected={state.warranty}
                                        label={"Garantia"}
                                        onChange={(date) => handleDate(date, "warranty")}
                                        dateFormat={'L'}
                                    />
                                </div>

                                <div className="col-span-2">
                                    <Attach onChangeFile={(file) => onChangeFile(file, "asset_file")}
                                            removeFile={() => removeFile("asset_file")}
                                            itemId={"asset_file"}
                                            attachId={state.asset_file}/>
                                </div>
                            </SentryRow>
                        </div>
                    </Card.Body>
                </Card>

                <Card className={"mt-2"}>
                    <Card.Body>
                        <div className="w-full flex justify-end">
                            <div className="flex gap-2">
                                {
                                    id &&
                                    <>
                                        <Button target="danger" onClick={destroy} actionFeedbackType='delete'>

                                            Excluir
                                        </Button>
                                    </>
                                }

                                <Button onClick={save} actionFeedbackType='save'>

                                    Salvar
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default AssetActivesForm;